import { createAsyncThunk } from "@reduxjs/toolkit";
import { OrderAndSalesAxios } from "../../api/orderandsalesaxios";
import { API_ENDPOINT } from "../../helpers/Endpoints/endpoints";
import { FilterBuilder } from "helpers/ThunkHelper/FilterBuilder";
import { parse } from "date-fns";
import { convertLocalDateToUTC } from "../../helpers/DateUtils/dateTimezoneUtils";
import { DEFAULT_ORGANISATION } from "pages/QuotationV2/Form/serviceInitialValues";

/**
 * Normalize bill_from so listing always gets organization_name.
 * Empty objects and bare strings are converted to a proper org payload.
 */
const normalizeBillFrom = (billFrom: any) => {
  if (!billFrom) return DEFAULT_ORGANISATION;

  if (typeof billFrom === "string") {
    try {
      const parsed = JSON.parse(billFrom);
      if (
        parsed &&
        typeof parsed === "object" &&
        !Array.isArray(parsed) &&
        Object.keys(parsed).length > 0
      ) {
        return parsed;
      }
    } catch {
      // leave as string address — not usable for organisation_name
    }
    return DEFAULT_ORGANISATION;
  }

  if (
    typeof billFrom === "object" &&
    !Array.isArray(billFrom) &&
    Object.keys(billFrom).length > 0
  ) {
    return billFrom;
  }

  return DEFAULT_ORGANISATION;
};

// --- PRODUCT QUOTATION THUNKS ---

/**
 * Helper function to convert date string to UTC ISO string
 * Handles yyyy-MMM-dd format (e.g., "2026-Jan-14") and YYYY-MM-DD format
 * Converts local date to UTC ISO string for backend
 */
const convertToUTC = (dateStr: string | undefined): string => {
  if (!dateStr) return "";
  try {
    let localDateStr = "";
    
    // Try parsing yyyy-MMM-dd format first (e.g., "2026-Jan-14")
    let parsed = parse(dateStr, "yyyy-MMM-dd", new Date());
    if (!isNaN(parsed.getTime())) {
      // Convert to YYYY-MM-DD format
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      const day = String(parsed.getDate()).padStart(2, "0");
      localDateStr = `${year}-${month}-${day}`;
    }
    // Try parsing YYYY-MM-DD format (e.g., "2026-01-14")
    else if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      localDateStr = dateStr;
    }
    // Fallback: try parsing as ISO string - if already UTC, return as is
    else if (dateStr.includes("T") || dateStr.includes("Z")) {
      // Already in UTC format, return as is
      return dateStr;
    }
    
    // Convert local date string to UTC ISO string
    if (localDateStr) {
      return convertLocalDateToUTC(localDateStr);
    }
    
    return "";
  } catch {
    return "";
  }
};

/**
 * Thunk to fetch a single Product Quotation by its ID.
 * @param id - The UUID of the quotation.
 */
export const downloadProductQuotationPdf = createAsyncThunk(
  "quotation/downloadProductQuotationPdf",
  async (
    { id, quotationNumber, withSignature }: { id: string; quotationNumber: string; withSignature?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await OrderAndSalesAxios.get(
        `${API_ENDPOINT.QUOTATION.PRODUCT.DOWNLOAD_PDF(id)}${withSignature !== undefined ? `?withSignature=${withSignature}` : ""}`,
        {
          responseType: "blob", // Important for handling binary file data
        }
      );

      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Product_Quotation_${quotationNumber}.pdf`); // Set the file name
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // Clean up the URL object

      return { success: true };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to download PDF");
    }
  }
);

/**
 * PATCH quotation email details (subject & body for PDF/email).
 */
export const updateProductQuotationEmailDetails = createAsyncThunk(
  "quotation/updateProductQuotationEmailDetails",
  async (
    {
      id,
      pdf_email_subject,
      pdf_email_body,
    }: {
      id: string;
      pdf_email_subject?: string | null;
      pdf_email_body?: string | null;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await OrderAndSalesAxios.patch(
        API_ENDPOINT.QUOTATION.PRODUCT.EMAIL_DETAILS(id),
        { pdf_email_subject: pdf_email_subject ?? null, pdf_email_body: pdf_email_body ?? null }
      );
      return response.data?.data ?? response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to update email details");
    }
  }
);

/**
 * Thunk to fetch a single Product Quotation by its ID.
 * @param id - The UUID of the quotation.
 */
export const fetchProductQuotationById = createAsyncThunk(
  "quotation/fetchProductQuotationById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await OrderAndSalesAxios.get(
        API_ENDPOINT.QUOTATION.PRODUCT.DETAIL(id)
      );
      // The backend returns the object in the format: { status: 200, success: true, message: "...", data: {...} }
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Transform frontend form data to backend API payload format for product quotation
 */
const transformProductQuotationData = (
  formData: any,
  quotationStatus: string = "Prepared",
  quotationStage: string = "Draft"
) => {
  const {
    quotation_date,
    valid_until,
    enquiry_reference_number,
    enquiry_product_id,
    initiated_by,
    company,
    contact_persons,
    agent_name,
    members,
    currency, // Field doesn't exist in QuotationService model
    currency_symbol, // Field doesn't exist in QuotationService model
    exchange_rate, // Field doesn't exist in QuotationService model
    mode_of_emd, // Field doesn't exist in QuotationService model
    emd_amount, // Field doesn't exist in QuotationService model
    inspection, // Field doesn't exist in QuotationService model
    inspection_date, // Field doesn't exist in QuotationService model
    installation, // Field doesn't exist in QuotationService model
    installation_date, // Field doesn't exist in QuotationService model
    products,
    terms_and_conditions,
    notes,
    next_follow_up_date,
    follow_up_remarks,
    reference_number,
    tags,
  } = formData;

  // Always sync selected contact persons into customer_details on save
  const mappedContacts = (contact_persons || []).map((contact: any) => ({
    contact_id: contact.id || contact.contact_id || undefined,
    name: contact.name || contact.contact_name || "",
    designation: contact.designation || undefined,
    phone_country: contact.phone_country || undefined,
    phone_number: contact.phone_number || undefined,
    email: contact.email || undefined,
  }));

  const customer_details = formData.customer_details
    ? {
        ...formData.customer_details,
        contacts: mappedContacts,
        contact_details: mappedContacts,
        contact_person_name: undefined,
        contact_person_phone_no: undefined,
        contact_person_email: undefined,
        contact_person_designation: undefined,
      }
    : company
      ? {
          company_id: company.company_id,
          company_name: company.company_name,
          company_email: company.company_email || "",
          vendor_code: company.vendor_code || "",
          branch_name: company.branch_name || "",
          phone_number: company.phone_number || "",
          gstin: company.gstin || "",
          gst_type: company.gst_type || "",
          industry: company.industry || "",
          billing_address: company.billing_address || {},
          shipping_addresses: company.shipping_addresses || [],
          contacts: mappedContacts,
          contact_details: mappedContacts,
        }
      : undefined;

  // Transform assigned members
  const assigned_members =
    members?.map((member: any) => ({
      user_id: member.id,
      username: member.name,
      first_name: member.first_name ?? member.name,
      last_name: member.last_name ?? "",
    })) || [];

  // Transform products to quotation_product_items - filter out invalid entries
  const quotation_product_items =
    products
      ?.filter(
        (product: any) =>
          (product.id || product.product_id) && // Ensure product has an ID
          product.product_name && // Ensure required fields are present
          product.category_name &&
          product.make &&
          product.model &&
          product.unit &&
          product.hsn_code
      )
      .map((product: any) => {
        // Transform shipping address to proper format
        let shippingAddress = null;
        if (product.shipping_address) {
          // If it's an object (full address object), use it directly
          if (typeof product.shipping_address === "object" && !Array.isArray(product.shipping_address)) {
            // Check if it's a complete address object with shipping_address_id
            if (product.shipping_address.shipping_address_id) {
              // It's a complete object - use it directly, but ensure all fields are present
              shippingAddress = {
                shipping_address_id: product.shipping_address.shipping_address_id,
                company_id: product.shipping_address.company_id || customer_details?.company_id,
                address: product.shipping_address.address,
                city: product.shipping_address.city,
                state: product.shipping_address.state,
                country: product.shipping_address.country || "India",
                pincode: product.shipping_address.pincode || product.shipping_address.pin_code,
                phone_number: product.shipping_address.phone_number || "",
                phone_country: product.shipping_address.phone_country || "+91",
                is_default: product.shipping_address.is_default || false,
                created_at: product.shipping_address.created_at,
                updated_at: product.shipping_address.updated_at
              };
            } else if (product.shipping_address.address && customer_details?.shipping_addresses?.length > 0) {
              // Partial object (only has address field) - try to find complete one from customer_details
              const matchingAddr = customer_details.shipping_addresses.find(
                (addr: any) => {
                  // Try to match by address string
                  const addrString = `${addr.address}, ${addr.city}, ${addr.state}, ${addr.country} - ${addr.pincode}`;
                  return addrString === product.shipping_address.address || 
                         addr.address === product.shipping_address.address ||
                         (product.shipping_address.shipping_address_id && addr.shipping_address_id === product.shipping_address.shipping_address_id);
                }
              );
              if (matchingAddr) {
                shippingAddress = matchingAddr;
              } else {
                // Couldn't find match - use partial object (shouldn't happen in normal flow)
                console.warn("Could not find complete shipping address for:", product.shipping_address);
                shippingAddress = product.shipping_address;
              }
            } else {
              // No customer_details or no match - use the object as-is (shouldn't happen)
              console.warn("Incomplete shipping address object:", product.shipping_address);
              shippingAddress = product.shipping_address;
            }
          } 
          // If it's an array, check if elements are objects or strings
          else if (Array.isArray(product.shipping_address)) {
            // If array has objects, use the first one; if strings, convert to object
            if (product.shipping_address.length > 0) {
              const firstAddr = product.shipping_address[0];
              if (typeof firstAddr === "object" && firstAddr.shipping_address_id) {
                shippingAddress = firstAddr;
              } else if (typeof firstAddr === "string") {
                // Try to find matching address from customer_details
                if (customer_details?.shipping_addresses?.length > 0) {
                  const matchingAddr = customer_details.shipping_addresses.find(
                    (addr: any) => addr.address === firstAddr
                  );
                  shippingAddress = matchingAddr || { address: firstAddr };
                } else {
                  shippingAddress = { address: firstAddr };
                }
              }
            }
          } 
          // If it's a string (legacy format), try to find matching address from customer_details
          else if (typeof product.shipping_address === "string") {
            if (customer_details?.shipping_addresses?.length > 0) {
              const matchingAddr = customer_details.shipping_addresses.find(
                (addr: any) => {
                  const addrString = `${addr.address}, ${addr.city}, ${addr.state}, ${addr.country} - ${addr.pincode}`;
                  return addrString === product.shipping_address || addr.address === product.shipping_address;
                }
              );
              shippingAddress = matchingAddr || { address: product.shipping_address };
            } else {
              shippingAddress = { address: product.shipping_address };
            }
          }
        } 
        // Fallback to customer's shipping addresses if no address provided
        else if (customer_details?.shipping_addresses?.length > 0) {
          shippingAddress = customer_details.shipping_addresses[0]; // Use first one as object
        }

        return {
          product_id: product.product_id || product.id,
          product_name: product.product_name,
          category_name:
            typeof product.category_name === "object"
              ? product.category_name.category_name
              : product.category_name,
          sub_category_name:
            typeof product.sub_category_name === "object"
              ? product.sub_category_name.sub_category_name
              : product.sub_category_name,
          product_type: Array.isArray(product.product_type)
            ? product.product_type.join(", ")
            : product.type || product.product_type || "",
          make_name: product.make,
          model_name: product.model,
          unit_name: product.unit,
          unit_price: parseFloat(product.unit_price),
          hsn_code: product.hsn_code,
          gst_percentage: parseFloat(product.gst_percentage),
          gst_value: parseFloat(product.gst_value),
          remark: product.remark || undefined,
          product_sku: product.sku,
          product_quantity: parseInt(product.quantity),
          product_total: parseFloat(product.total),
          product_net_total: parseFloat(product.net_value),
          serial_number: product.serial_number || undefined,
          discount_type: product.discount_type || "percentage",
          // IMPORTANT: discount_value must be the RAW input value (not calculated)
          // Backend expects raw value: 10 for 10% or 500 for ₹500
          // calculated_discount_value is frontend-only and should NEVER be sent to backend
          // Safeguard: Explicitly use discount_value (raw) and NEVER calculated_discount_value
          discount_value: (() => {
            // Always use the raw discount_value from the form state
            // NEVER use calculated_discount_value - it's frontend-only
            const rawValue = parseFloat(product.discount_value || 0);
            const calculatedValue = parseFloat(product.calculated_discount_value || 0);
            
            // Safety check: If discount_value appears to be the calculated value, log warning
            // For currency: raw should typically be <= calculated (unless user entered more than gross)
            // For percentage: raw should be <= 100, calculated should be much larger
            if (calculatedValue > 0) {
              if (product.discount_type === "currency" && Math.abs(rawValue - calculatedValue) < 0.01) {
                console.warn(`[DISCOUNT SAFEGUARD] Product "${product.product_name}": discount_value (${rawValue}) matches calculated_discount_value (${calculatedValue}). This may indicate discount_value contains calculated value instead of raw input.`);
              }
              // For percentage: if raw > 100, it's definitely wrong
              if (product.discount_type === "percentage" && rawValue > 100) {
                console.warn(`[DISCOUNT SAFEGUARD] Product "${product.product_name}": discount_value (${rawValue}) is > 100 for percentage type. This is invalid.`);
              }
            }
            
            // Always return the raw value (even if it seems wrong, we can't fix it here)
            return rawValue;
          })(),
          serviceable: product.is_serviceable,
          rate_in_currency: parseFloat(product.rate_in_currency) || undefined,
          gross_value: parseFloat(product.gross_value) || undefined,
          shipping_address: shippingAddress,
          prod_description: product.prod_description ?? product.description ?? undefined,
          additional_details: product.additional_details ?? undefined,
        };
      }) || [];

  // Transform terms and conditions - filter out invalid entries
  const terms_and_conditions_transformed =
    terms_and_conditions
      ?.filter(
        (term: any) =>
          term.type &&
          term.type.trim() !== "" &&
          term.option &&
          term.option.trim() !== ""
      )
      .map((term: any) => ({
        type: term.type,
        options: term.description || term.option,
      })) || [];

  // Helper function to round to 2 decimal places
  const roundToTwoDecimals = (num: number): number => Math.round(num * 100) / 100;

  // Calculate totals with proper decimal handling (these should be calculated in the form, but we'll use the products data)
  const sub_total_before_discount = roundToTwoDecimals(
    products?.reduce(
      (sum: number, product: any) => {
        const unitPrice = parseFloat(product.unit_price || "0");
        const quantity = parseFloat(product.quantity || "1"); // Preserve decimal quantities
        return sum + (unitPrice * quantity);
      },
      0
    ) || 0
  );
  const total_discount_value = roundToTwoDecimals(
    products?.reduce(
      (sum: number, product: any) => sum + parseFloat(product.discount_value || "0"),
      0
    ) || 0
  );
  const total_gst_value = roundToTwoDecimals(
    products?.reduce(
      (sum: number, product: any) => sum + parseFloat(product.gst_value || "0"),
      0
    ) || 0
  );
  const total_net_value = roundToTwoDecimals(
    products?.reduce(
      (sum: number, product: any) => sum + parseFloat(product.net_value || "0"),
      0
    ) || 0
  );
  const grand_total_amount = roundToTwoDecimals(
    products?.reduce(
      (sum: number, product: any) => sum + parseFloat(product.total || "0"),
      0
    ) || 0
  );

  return {
    quotation_date: convertToUTC(quotation_date) || undefined,
    valid_until_date: convertToUTC(valid_until) || undefined,
    initiated_by,
    enquiry_product_id: enquiry_product_id || undefined,
    enquiry_number: enquiry_reference_number || undefined,
    reference_number: reference_number || undefined,
    quotation_stage: quotationStage,
    agent_name: agent_name || undefined,
    bill_from: normalizeBillFrom(formData.bill_from_address),
    quotation_status: quotationStatus,
    customer_details,
    assigned_members,
    currency_name: currency, // Field doesn't exist in QuotationService model
    currency_code: currency, // Field doesn't exist in QuotationService model
    // Use the selected currency symbol from the form (set by CurrencyExchangeSection).
    // Fallback to INR symbol, then "$" for legacy behavior.
    currency_symbol:
      (typeof currency_symbol === "string" && currency_symbol.trim() !== ""
        ? currency_symbol
        : currency === "INR"
          ? "₹"
          : "$"),
    currency_rate_in_inr:
      exchange_rate && exchange_rate !== ""
        ? parseFloat(exchange_rate)
        : undefined, // Field doesn't exist in QuotationService model
    mode_of_emd, // Field doesn't exist in QuotationService model
    emd_amount:
      emd_amount && emd_amount !== "" ? parseFloat(emd_amount) : undefined, // Field doesn't exist in QuotationService model
    inspection, // Field doesn't exist in QuotationService model
    inspection_date: convertToUTC(inspection_date) || undefined, // Field doesn't exist in QuotationService model
    installation, // Field doesn't exist in QuotationService model
    installation_date: convertToUTC(installation_date) || undefined, // Field doesn't exist in QuotationService model
    notes: notes || undefined,
    // attachments are handled separately as files in FormData
    terms_and_conditions: terms_and_conditions_transformed,
    sub_total_before_discount,
    total_discount_value,
    total_gst_value,
    total_net_value,
    grand_total_amount,
    quotation_product_items,
    tags: tags || [],
    next_follow_up_date: convertToUTC(next_follow_up_date) || undefined,
    follow_up_remarks: follow_up_remarks || undefined,
  };
};

/**
 * Thunk to create a new Product Quotation with file upload support.
 * @param payload - Object containing form data and quotation status/stage options.
 */
export const createProductQuotation = createAsyncThunk(
  "quotation/createProductQuotation",
  async (
    {
      formData,
      quotationStatus = "Prepared",
      quotationStage = "Draft",
    }: {
      formData: any;
      quotationStatus?: string;
      quotationStage?: string;
    },
    { rejectWithValue }
  ) => {
    console.log("payload in create product thunk : ", {
      formData,
      quotationStatus,
      quotationStage,
    });

    try {
      // Transform the frontend data to backend format
      const transformedData = transformProductQuotationData(
        formData,
        quotationStatus,
        quotationStage
      );

      // Create FormData for multipart upload
      const formDataPayload = new FormData();

      // Add all non-file data as JSON string
      Object.keys(transformedData).forEach((key) => {
        const value = (transformedData as any)[key];
        if (value !== undefined && value !== null) {
          if (typeof value === "object" && !(value instanceof Date)) {
            formDataPayload.append(key, JSON.stringify(value));
          } else if (value instanceof Date) {
            formDataPayload.append(key, value.toISOString());
          } else {
            formDataPayload.append(key, String(value));
          }
        }
      });

      // Add attachments. Existing enquiry URLs are sent as text fields so create can preserve them.
      if (formData.attachments && formData.attachments.length > 0) {
        formData.attachments.forEach((item: File | string) => {
          if (item instanceof File) {
            formDataPayload.append("attachments", item);
          } else if (typeof item === "string" && item.trim()) {
            formDataPayload.append("attachments", item);
          }
        });
      }

      const response = await OrderAndSalesAxios.post(
        API_ENDPOINT.QUOTATION.PRODUCT.CREATE,
        formDataPayload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Thunk to update an existing Product Quotation with file upload support.
 * @param data - Object containing { id, ...payload }
 */
export const updateProductQuotation = createAsyncThunk(
  "quotation/updateProductQuotation",
  async (
    {
      id,
      quotationStatus = "Prepared",
      quotationStage = "Draft",
      ...payload
    }: {
      id: string;
      quotationStatus?: string;
      quotationStage?: string;
      [key: string]: any;
    },
    { rejectWithValue }
  ) => {
    try {
      // Transform the frontend data to backend format (same as create)
      const transformedData = transformProductQuotationData(
        payload,
        quotationStatus,
        quotationStage
      );

      // Create FormData for multipart upload (consistent with create)
      const formDataPayload = new FormData();

      // Add all non-file data as JSON string
      Object.keys(transformedData).forEach((key) => {
        const value = (transformedData as any)[key];
        if (value !== undefined && value !== null) {
          if (typeof value === "object" && !(value instanceof Date)) {
            formDataPayload.append(key, JSON.stringify(value));
          } else if (value instanceof Date) {
            formDataPayload.append(key, value.toISOString());
          } else {
            formDataPayload.append(key, String(value));
          }
        }
      });

      // Add attachments (files) to FormData if present
      // Separate new files from existing URLs
      if (payload.attachments && payload.attachments.length > 0) {
        const newFiles: File[] = [];
        const existingUrls: string[] = [];
        
        payload.attachments.forEach((item: File | string) => {
          if (item instanceof File) {
            newFiles.push(item);
          } else if (typeof item === 'string') {
            existingUrls.push(item);
          }
        });
        
        // Append new files to FormData
        newFiles.forEach((file: File) => {
          formDataPayload.append("attachments", file);
        });
        
        // Send existing URLs as a separate field for backend to keep them
        if (existingUrls.length > 0) {
          formDataPayload.append("existing_attachments", JSON.stringify(existingUrls));
        }
      }

      const response = await OrderAndSalesAxios.put(
        API_ENDPOINT.QUOTATION.PRODUCT.UPDATE(id),
        formDataPayload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Thunk to fetch the list of all Product Quotations (e.g., for a table view).
 */
export const fetchAllProductQuotations = createAsyncThunk(
  "quotation/fetchAllProductQuotations",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    search_type,
    filters,
    type,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    search_type?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
    type?: any;
  }) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    params.append("quotation_stage", type);
    if (search) params.append("search", search);
    if (search_type) params.append("search_type", search_type);
    if (sort) {
      const orderingValue = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingValue);
    }
    const filterPayload = FilterBuilder(filters ?? []);

    const body = {
      filters: filterPayload,
      ...(search_type ? { search_type } : {}),
    };

    const response: any = await OrderAndSalesAxios.post(
      `${API_ENDPOINT.QUOTATION.PRODUCT.LIST}?${params.toString()}`,
      body
    );
    console.log("response in product quotation list: ", response);
    return {
      data: response.data.data || [],
      rowCount: response.data.count || 0,
    };
  }
);

export const fetchProductQuotationDynamicDropdown = createAsyncThunk(
  "quotaion/fetchProductQuotationDynamicDropdown",
  async (type: any, { rejectWithValue }) => {
    try {
      const response: any = await OrderAndSalesAxios.get(
        API_ENDPOINT.QUOTATION.PRODUCT.DROPDOWN
      );
      const rawData = response.data?.data || response.data || [];
      const normalizedData = Array.isArray(rawData)
        ? rawData.map((item: any) => ({
            ...item,
            id: item.id || item.quotation_product_id,
            product_quotation_name:
              item.product_quotation_name || item.quotation_number || "",
          }))
        : [];

      return {
        ...response.data,
        data:
          type === "quotation_number"
            ? normalizedData
            : response.data?.data || normalizedData,
      };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Service Enquiry dropdown"
      );
    }
  }
);

// THUNK TO HANDLE QUOTATION STAGE/STATUS CHANGE (E.G., MARK AS JUNK, CHANGE STATUS)
export const handleProductQuotationStatus = createAsyncThunk(
  "quotation/handleProductQuotationStatus",
  async (
    {
      id,
      quotation_status,
      quotation_stage,
      remark,
    }: {
      id: string;
      quotation_status?: string;
      quotation_stage?: string;
      remark?: string;
    },
    { rejectWithValue }
  ) => {
    const payload: any = { id };
    if (quotation_status) payload.quotation_status = quotation_status;
    if (quotation_stage) payload.quotation_stage = quotation_stage;
    if (remark) payload.remark = remark;

    try {
      const response = await OrderAndSalesAxios.patch(
        API_ENDPOINT.QUOTATION.PRODUCT.STATUS_UPDATE(id),
        payload
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Thunk to delete a Product Quotation by its ID.
 * @param id - The UUID of the quotation to delete.
 */
export const deleteProductQuotation = createAsyncThunk(
  "quotation/deleteProductQuotation",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await OrderAndSalesAxios.delete(
        API_ENDPOINT.QUOTATION.PRODUCT.DELETE(id)
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Thunk to export Product Quotations to CSV.
 * @param params - Object containing search, filters, and quotation_stage (type)
 */
export const exportProductQuotationCSV = async ({
  search,
  filters,
  type, // quotation_stage: "Draft" | "log"
}: {
  search?: string;
  filters?: any;
  type?: string;
}) => {
  const params = new URLSearchParams();
  params.append("export", "csv");
  if (type) params.append("quotation_stage", type);
  if (search) params.append("search", search);

  const filterPayload = FilterBuilder(filters ?? []);

  const body = {
    filters: filterPayload,
  };

  const response = await OrderAndSalesAxios.post(
    `${API_ENDPOINT.QUOTATION.PRODUCT.LIST}?${params.toString()}`,
    body,
    {
      responseType: "blob",
    }
  );

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "quotation_products.csv",
  };
};
